import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, phpFmt } from "../lib/api";
import { useCities } from "../lib/useCities";
import { useAuth, formatApiError } from "../lib/auth";
import ProtectedRoute from "../components/ProtectedRoute";
import { toast, Toaster } from "sonner";
import { X, Upload, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const AMENITY_OPTIONS = [
    "Mail Handling", "Call Answering", "Reception Services", "Meeting Room Credits",
    "Private Phone Booths", "High-Speed WiFi", "Printing", "Business-Grade Address",
    "24/7 Access", "Lockers", "Pantry", "Free Coffee",
];

function Stepper({ step }) {
    const steps = ["Center", "Rate & Contact", "Photos", "Review"];
    return (
        <div className="flex items-center gap-2 flex-wrap">
            {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                    <div className={`h-6 w-6 grid place-items-center text-[10px] font-medium ${i <= step ? "bg-zinc-950 text-white" : "bg-zinc-200 text-zinc-500"}`}>{i + 1}</div>
                    <span className={`text-[10px] uppercase tracking-label ${i <= step ? "text-zinc-950" : "text-zinc-400"}`}>{s}</span>
                    {i < steps.length - 1 && <span className="w-8 h-px bg-zinc-300" />}
                </div>
            ))}
        </div>
    );
}

function Body() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cities: cityNames } = useCities();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const [form, setForm] = useState({
        name: "",
        city: "Paranaque",
        public_area: "",
        description: "",
        amenities: [],
        photos: [],
        monthly_rate_php: 3800,
        street_address_private: "",
        contact_name_private: user?.contact_name || "",
        contact_email_private: user?.email || "",
        contact_phone_private: user?.contact_phone || "",
    });

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const toggleAmenity = (a) => {
        setForm((p) => ({
            ...p,
            amenities: p.amenities.includes(a) ? p.amenities.filter((x) => x !== a) : [...p.amenities, a],
        }));
    };

    const onFiles = async (files) => {
        const list = Array.from(files).slice(0, 6 - form.photos.length);
        try {
            const datas = await Promise.all(
                list.map((f) =>
                    new Promise((res, rej) => {
                        if (f.size > 3 * 1024 * 1024) return rej(new Error(`${f.name} is larger than 3MB`));
                        const r = new FileReader();
                        r.onload = () => res(r.result);
                        r.onerror = () => rej(new Error(`Failed to read ${f.name}`));
                        r.readAsDataURL(f);
                    })
                )
            );
            update("photos", [...form.photos, ...datas]);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const removePhoto = (i) => update("photos", form.photos.filter((_, idx) => idx !== i));

    const canNext = () => {
        if (step === 0) return form.name && form.city && form.public_area && form.description;
        if (step === 1) return form.monthly_rate_php > 0 && form.street_address_private && form.contact_name_private && form.contact_email_private && form.contact_phone_private;
        if (step === 2) return form.photos.length >= 1;
        return true;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post("/provider/centers", { ...form, monthly_rate_php: Number(form.monthly_rate_php) });
            setDone(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="pt-32 min-h-screen">
                <div className="mx-auto max-w-xl px-4 text-center">
                    <CheckCircle2 className="h-14 w-14 text-[#FF6A13] mx-auto" strokeWidth={1.5} />
                    <h1 className="font-display text-4xl font-semibold tracking-tight mt-6">Submitted for review</h1>
                    <p className="mt-3 text-zinc-600">
                        Thanks for enrolling your center. Our team reviews submissions within 48 hours. You'll see the status on your dashboard.
                    </p>
                    <button data-testid="go-to-provider-dashboard" onClick={() => navigate("/dashboard/provider")}
                        className="mt-8 bg-zinc-950 text-white px-6 py-3 hover:bg-[#FF6A13] transition-colors">
                        Go to dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-24 min-h-screen">
            <Toaster richColors position="top-center" />
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-[10px] uppercase tracking-label text-zinc-500">Enroll a Center</div>
                <h1 className="font-display text-5xl font-semibold tracking-tighter mt-2">List your workspace.</h1>
                <div className="mt-6"><Stepper step={step} /></div>

                <div className="mt-10 border border-zinc-200 bg-white p-6 md:p-8">
                    {step === 0 && (
                        <div className="space-y-5">
                            <div>
                                <label className="text-[10px] uppercase tracking-label text-zinc-500">Center name</label>
                                <input data-testid="enroll-name" value={form.name} onChange={(e) => update("name", e.target.value)}
                                    className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" placeholder="Loud Crowd Coworking" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">City</label>
                                    <select data-testid="enroll-city" value={form.city} onChange={(e) => update("city", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none">
                                        {cityNames.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Public area (shown to clients)</label>
                                    <input data-testid="enroll-public-area" value={form.public_area} onChange={(e) => update("public_area", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" placeholder="Aseana City, Paranaque" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-label text-zinc-500">Description</label>
                                <textarea data-testid="enroll-description" value={form.description} onChange={(e) => update("description", e.target.value)}
                                    rows={4} className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none"
                                    placeholder="What makes your address distinctive?" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-label text-zinc-500">Amenities</label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {AMENITY_OPTIONS.map((a) => (
                                        <button key={a} type="button" data-testid={`amenity-${a}`}
                                            onClick={() => toggleAmenity(a)}
                                            className={`text-[10px] uppercase tracking-label px-3 py-1.5 border ${form.amenities.includes(a) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"}`}>
                                            {a}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-5">
                            <div className="bg-orange-50 border-l-2 border-[#FF6A13] p-3 text-xs text-zinc-800">
                                Set your own monthly rate. Your contact details below stay private — FWG fronts all listings for clients.
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-label text-zinc-500">Monthly rate (PHP)</label>
                                <div className="mt-1 flex items-stretch">
                                    <span className="bg-zinc-950 text-white px-3 grid place-items-center font-display text-sm">₱</span>
                                    <input data-testid="enroll-rate" type="number" min={1} value={form.monthly_rate_php}
                                        onChange={(e) => update("monthly_rate_php", e.target.value === "" ? "" : Number(e.target.value))}
                                        className="flex-1 border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none" />
                                    <span className="bg-zinc-100 px-3 grid place-items-center text-[10px] uppercase tracking-label text-zinc-500">per month</span>
                                </div>
                                {form.monthly_rate_php > 0 && (
                                    <div className="text-[11px] text-zinc-500 mt-1">
                                        12 PDCs = {phpFmt(form.monthly_rate_php * 12)} · Cash (11 months) = {phpFmt(form.monthly_rate_php * 11)}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-label text-zinc-500">Real street address (private)</label>
                                <input data-testid="enroll-street" value={form.street_address_private} onChange={(e) => update("street_address_private", e.target.value)}
                                    className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Contact name</label>
                                    <input data-testid="enroll-contact-name" value={form.contact_name_private} onChange={(e) => update("contact_name_private", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Contact phone</label>
                                    <input data-testid="enroll-contact-phone" value={form.contact_phone_private} onChange={(e) => update("contact_phone_private", e.target.value)}
                                        className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" placeholder="+63 ..." />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-label text-zinc-500">Contact email</label>
                                <input data-testid="enroll-contact-email" type="email" value={form.contact_email_private} onChange={(e) => update("contact_email_private", e.target.value)}
                                    className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none" />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <div className="text-sm text-zinc-600">Upload up to <strong>6 photos</strong> of your center. JPEG/PNG, max 3MB each.</div>
                            <label htmlFor="photo-upload" data-testid="photo-upload-label"
                                className="mt-4 cursor-pointer block border-2 border-dashed border-zinc-300 hover:border-zinc-950 p-10 text-center transition-colors">
                                <Upload className="h-6 w-6 mx-auto text-zinc-500" strokeWidth={1.5} />
                                <div className="mt-3 font-display text-lg">Click to upload</div>
                                <div className="mt-1 text-xs text-zinc-500">{form.photos.length} / 6 uploaded</div>
                                <input id="photo-upload" data-testid="enroll-photos" type="file" accept="image/*" multiple
                                    className="hidden" onChange={(e) => onFiles(e.target.files)} />
                            </label>
                            {form.photos.length > 0 && (
                                <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2">
                                    {form.photos.map((p, i) => (
                                        <div key={i} className="relative aspect-square overflow-hidden bg-zinc-100">
                                            <img src={p} alt="" className="h-full w-full object-cover" />
                                            <button data-testid={`remove-photo-${i}`} onClick={() => removePhoto(i)}
                                                className="absolute top-1 right-1 h-6 w-6 bg-black/70 text-white grid place-items-center">
                                                <X className="h-3 w-3" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-label text-zinc-500">Center</div>
                                <div className="font-display text-2xl font-semibold">{form.name}</div>
                                <div className="text-sm text-zinc-600">{form.city} · {form.public_area}</div>
                                <div className="mt-2 text-sm text-zinc-700">{form.description}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-label text-zinc-500">Monthly rate</div>
                                <div className="font-display text-3xl font-semibold">{phpFmt(form.monthly_rate_php)}</div>
                            </div>
                            {form.amenities.length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-label text-zinc-500 mb-2">Amenities</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {form.amenities.map((a) => (
                                            <span key={a} className="text-[10px] uppercase tracking-label bg-zinc-100 px-2 py-1">{a}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {form.photos.length > 0 && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-label text-zinc-500 mb-2">Photos</div>
                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                        {form.photos.map((p, i) => (
                                            <div key={i} className="aspect-square overflow-hidden bg-zinc-100">
                                                <img src={p} alt="" className="h-full w-full object-cover" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="text-xs text-zinc-500">Submitting sends your center to FWG for review. You'll get a notification when it's live.</div>
                        </div>
                    )}

                    <div className="mt-10 flex items-center justify-between">
                        <button data-testid="step-back-btn" onClick={() => setStep((s) => Math.max(0, s - 1))}
                            disabled={step === 0}
                            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950 disabled:opacity-30">
                            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
                        </button>
                        {step < 3 ? (
                            <button data-testid="step-next-btn" onClick={() => canNext() && setStep((s) => s + 1)}
                                disabled={!canNext()}
                                className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm hover:bg-[#FF6A13] transition-colors disabled:opacity-40">
                                Continue <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        ) : (
                            <button data-testid="enroll-submit-btn" onClick={submit} disabled={submitting}
                                className="inline-flex items-center gap-2 bg-[#FF6A13] text-white px-5 py-3 text-sm hover:bg-orange-600 transition-colors disabled:opacity-40">
                                {submitting ? "Submitting…" : "Submit for review"} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function ProviderEnroll() {
    return (
        <ProtectedRoute roles={["provider", "admin"]}>
            <Body />
        </ProtectedRoute>
    );
}
